import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { deleteSkill, unpublishSkill } from "@/api/skills";
import { AdminPageContent } from "@/components/admin";
import type {
    EditorHeading,
    MarkdownEditorHandle,
} from "@/components/shared/MarkdownEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

import { SkillEditorPane } from "./components/SkillEditorPane";
import {
    SkillFileTree,
    type SkillFileTreeOutlineHeading,
} from "./components/SkillFileTree";
import { SkillVersionHistory } from "./components/SkillVersionHistory";
import type { EntityComboboxValidity } from "@/components/shared/OrganizationCombobox";
import { SkillSettingsPopover } from "./components/SkillSettingsPopover";
import { sortSkillVersionsByRecency } from "./components/skill-version-order";
import {
    isSkillIndexField,
    isSkillSettingsField,
    useSkillEditor,
    useStableStringList,
} from "./components/useSkillEditor";
import { VersionDiffModal } from "./components/VersionDiffModal";
import { ReferenceDeleteDialog } from "./components/dialogs/ReferenceDeleteDialog";
import { SaveConflictDialog } from "./components/dialogs/SaveConflictDialog";
import { SaveRecoveryDialog } from "./components/dialogs/SaveRecoveryDialog";
import { ReferenceRenameDialog } from "./components/dialogs/ReferenceRenameDialog";
import { SkillRenameDialog } from "./components/dialogs/SkillRenameDialog";
import { SkillPublishDialog } from "./components/dialogs/SkillPublishDialog";
import { SKILL_STATUS_MAP } from "./skill-helpers";
import { SKILL_INDEX_CLIENT_KEY } from "./skill-editor-references";
import { isSkillEditorFileDirty } from "./skill-editor-save";
import {
    getSkillEditorScopeAssignment,
    isSkillEditorScopeDirty,
} from "./skill-editor-state";

interface PendingSkillDelete {
    skillId: number;
    name: string;
    isPublished: boolean;
}

interface SkillLifecycleMutationTarget {
    skillId: number;
    detailsQueryKey: readonly ["admin-skill-details", string];
}

const VALIDATION_SUMMARY_ID = "skill-editor-validation-summary";
const SCOPE_BLOCK_SUMMARY_ID = "skill-editor-scope-block-summary";
const BROKEN_REFERENCES_SUMMARY_ID = "skill-editor-broken-references-summary";
const SNAPSHOT_DEBT_SUMMARY_ID = "skill-editor-snapshot-debt-summary";
const RETRY_BLOCKED_SUMMARY_ID = "skill-editor-retry-blocked-summary";
const PUBLISH_UNKNOWN_SUMMARY_ID = "skill-editor-publish-unknown-summary";
const PUBLISH_REJECTED_SUMMARY_ID = "skill-editor-publish-rejected-summary";
const PUBLISH_UNPUBLISHED_SUMMARY_ID =
    "skill-editor-publish-unpublished-summary";

const AdminSkillDetails = () => {
    const { skillId = "" } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setBreadcrumbs } = useBreadcrumb();
    const paneRegionRef = useRef<HTMLElement>(null);
    const markdownEditorRef = useRef<MarkdownEditorHandle>(null);
    const actionsTriggerRef = useRef<HTMLButtonElement>(null);
    const renameReturnFocusRef = useRef<HTMLButtonElement | null>(null);
    const [scopeValidity, setScopeValidity] =
        useState<EntityComboboxValidity>("valid");
    const [focusRequest, setFocusRequest] = useState(0);
    const [publishOpen, setPublishOpen] = useState(false);
    const [reloadFailed, setReloadFailed] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyVersionId, setHistoryVersionId] = useState<number | null>(
        null
    );
    const [renameOpen, setRenameOpen] = useState(false);
    const [unpublishOpen, setUnpublishOpen] = useState(false);
    const [deleteSkillTarget, setDeleteSkillTarget] =
        useState<PendingSkillDelete | null>(null);
    const [pendingReferenceDelete, setPendingReferenceDelete] = useState<
        string | null
    >(null);
    const [outlineHeadings, setOutlineHeadings] = useState<
        SkillFileTreeOutlineHeading[]
    >([]);
    const editor = useSkillEditor(skillId);
    const {
        skill,
        state,
        diff,
        validation,
        references,
        recognitionVocabulary,
        renameRequest,
        displayFiles,
        selectedFile,
        coreCount,
        coreCountScope,
        coreCountError,
        coreIncludesThisSkill,
        saveChanges,
        publishFailureReason,
        canRetryPublishOnly,
        versions,
        versionsError,
        isVersionsLoading,
        retryVersions,
        isDirty,
        hasDiscardableChanges,
        hasPendingSnapshot,
        isSaving,
        saveError,
        saveStatus,
        saveConflict,
        saveRecovery,
        isResolvingRecovery,
        clearSaveError,
        updateFields,
        addFile,
        updateFile,
        deleteFile,
        selectFile,
        requestRenameCommit,
        acknowledgeRename,
        cancelRename,
        getReferenceCount,
        getInboundSources,
        getBrokenTargets,
        discard,
        save,
        reloadConflicts,
        clearSaveConflict,
        keepLocal,
        reloadLatest,
        retryPublishOnly,
        dismissPublishFailure,
        recreateUnderSameName,
        renameAndRecreate,
        discardLocal,
        reloadServerFile,
        keepLocalAndRename,
    } = editor;
    const latestVersionNumber = useMemo(
        () =>
            versionsError
                ? null
                : (sortSkillVersionsByRecency(versions ?? [])[0]
                      ?.version_number ?? null),
        [versions, versionsError]
    );
    const summaryVersionNumber =
        hasPendingSnapshot || isSaving ? null : latestVersionNumber;
    const latestVersionSuffix =
        summaryVersionNumber === null
            ? ""
            : ` · latest version v${summaryVersionNumber}`;
    const changeSummary =
        saveChanges.length > 0
            ? `${saveChanges.length} ${
                  saveChanges.length === 1 ? "change" : "changes"
              }${latestVersionSuffix}`
            : hasDiscardableChanges
              ? "Unsaved changes"
              : summaryVersionNumber === null
                ? "All changes saved"
                : `Saved${latestVersionSuffix}`;
    const detailsQueryKey = useMemo(
        () => ["admin-skill-details", skillId] as const,
        [skillId]
    );
    const activeDetailsQueryKeyRef = useRef(detailsQueryKey);
    activeDetailsQueryKeyRef.current = detailsQueryKey;
    const unpublishMutation = useMutation({
        mutationFn: ({ skillId }: SkillLifecycleMutationTarget) =>
            unpublishSkill(skillId),
        onSuccess: async (updatedSkill, target) => {
            queryClient.setQueryData(target.detailsQueryKey, updatedSkill);
            if (
                activeDetailsQueryKeyRef.current[1] ===
                target.detailsQueryKey[1]
            ) {
                setUnpublishOpen(false);
                toast.success("Skill unpublished");
            }
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["admin-skills"] }),
                queryClient.invalidateQueries({
                    queryKey: ["admin-skills-all"],
                }),
                queryClient.invalidateQueries({
                    queryKey: ["admin-skills-core-count"],
                }),
            ]);
        },
    });
    const deleteSkillMutation = useMutation({
        mutationFn: ({ skillId }: SkillLifecycleMutationTarget) =>
            deleteSkill(skillId),
        onSuccess: async (_, target) => {
            if (
                activeDetailsQueryKeyRef.current[1] ===
                target.detailsQueryKey[1]
            ) {
                setDeleteSkillTarget(null);
                toast.success("Skill deleted");
                navigate("/admin/skills");
            }
            await queryClient.invalidateQueries({
                queryKey: target.detailsQueryKey,
                exact: true,
                refetchType: "none",
            });
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["admin-skills"] }),
                queryClient.invalidateQueries({
                    queryKey: ["admin-skills-all"],
                }),
                queryClient.invalidateQueries({
                    queryKey: ["admin-skills-core-count"],
                }),
            ]);
        },
    });

    useDocumentTitle("Skill");

    useEffect(() => {
        setPublishOpen(false);
        setHistoryOpen(false);
        setHistoryVersionId(null);
        setRenameOpen(false);
        setUnpublishOpen(false);
        setDeleteSkillTarget(null);
        setPendingReferenceDelete(null);
    }, [skill.id]);

    useEffect(() => {
        setBreadcrumbs([
            { label: "Admin", path: "home" },
            { label: "Skills", path: "skills" },
            { label: skill.name },
        ]);
    }, [setBreadcrumbs, skill.name]);

    useEffect(() => {
        if (focusRequest === 0) return;
        paneRegionRef.current
            ?.querySelector<HTMLInputElement>(
                'input[aria-label="Reference name"]'
            )
            ?.focus();
    }, [focusRequest]);

    const indexDirty = diff.skillFields.some(isSkillIndexField);
    const skillSettingsDirty = diff.skillFields.some(isSkillSettingsField);
    const referenceNames = useStableStringList(
        displayFiles
            .map(({ name }) => name.trim())
            .filter((name) => name !== "")
    );
    const referenceErrors = useMemo(() => {
        const errors = new Map<
            string,
            { name?: string; content?: string; rename?: string }
        >();
        for (const issue of validation.issues) {
            const current = errors.get(issue.clientKey) ?? {};
            current[issue.field] ??= issue.message;
            errors.set(issue.clientKey, current);
        }
        return errors;
    }, [validation.issues]);
    const descriptionError = state.buffer.skill.description.trim()
        ? undefined
        : "Describe when this skill should be used";
    const hasValidationErrors =
        Boolean(descriptionError) ||
        state.buffer.skill.name.trim() === "" ||
        !validation.isValid;
    const validationTargets = Array.from(
        new Set([
            ...(state.buffer.skill.name.trim() === "" ? ["skill name"] : []),
            ...(descriptionError ? ["SKILL.md description"] : []),
            ...displayFiles.flatMap((file) => {
                const errors = referenceErrors.get(file.clientKey);
                return errors?.name || errors?.content
                    ? [file.name.trim() || "Untitled reference"]
                    : [];
            }),
        ])
    );
    const renameTargets = displayFiles.flatMap((file) =>
        referenceErrors.get(file.clientKey)?.rename
            ? [file.name.trim() || "Untitled reference"]
            : []
    );
    const validationSummary = hasValidationErrors
        ? [
              validationTargets.length > 0
                  ? `Fix validation errors before saving: ${validationTargets.join(", ")}.`
                  : undefined,
              renameTargets.length > 0
                  ? `Commit reference renames before saving: ${renameTargets.join(", ")}.`
                  : undefined,
          ]
              .filter((message): message is string => message !== undefined)
              .join(" ")
        : undefined;
    const brokenReferenceCount = references.brokenTargets.length;
    const scopeKind = state.buffer.skill.scopeKind;
    const scopeAssignment = getSkillEditorScopeAssignment(state.buffer.skill);
    const scopeEntityId =
        scopeAssignment.organizationId ?? scopeAssignment.organizationGroupId;
    const scopeDirty = isSkillEditorScopeDirty(
        state.buffer.skill,
        state.baseline.skill
    );
    const scopeBlockReason =
        scopeKind === "global"
            ? undefined
            : scopeEntityId === null
              ? scopeKind === "organization"
                  ? "Choose an organization."
                  : "Choose a group."
              : scopeDirty && scopeValidity !== "valid"
                ? scopeValidity === "not-found"
                    ? scopeKind === "organization"
                        ? "The selected organization no longer exists."
                        : "The selected group no longer exists."
                    : scopeKind === "organization"
                      ? "Confirm the selected organization before saving."
                      : "Confirm the selected group before saving."
                : undefined;
    const scopeBlocked = scopeBlockReason !== undefined;
    const validationDescription = [
        validationSummary ? VALIDATION_SUMMARY_ID : undefined,
        scopeBlocked ? SCOPE_BLOCK_SUMMARY_ID : undefined,
    ]
        .filter((id): id is string => id !== undefined)
        .join(" ");
    const publishBlockDescription = [
        validationDescription || undefined,
        brokenReferenceCount > 0 ? BROKEN_REFERENCES_SUMMARY_ID : undefined,
    ]
        .filter((id): id is string => id !== undefined)
        .join(" ");
    const selectedBaseline = selectedFile
        ? state.baseline.files.find(
              ({ clientKey }) => clientKey === selectedFile.clientKey
          )
        : undefined;
    const selectedReferenceErrors = selectedFile
        ? referenceErrors.get(selectedFile.clientKey)
        : undefined;
    const selectedIsDirty =
        skillSettingsDirty ||
        (selectedFile
            ? isSkillEditorFileDirty(state, selectedFile.clientKey)
            : indexDirty);
    const selectedStatus =
        selectedFile?.serverId === null
            ? "new"
            : selectedIsDirty
              ? "unsaved"
              : "saved";
    const pendingReference = pendingReferenceDelete
        ? displayFiles.find(
              ({ clientKey }) => clientKey === pendingReferenceDelete
          )
        : undefined;

    const inboundLinksFor = (clientKey: string) =>
        getInboundSources(clientKey).map(({ sourceClientKey }) => ({
            clientKey: sourceClientKey,
            name:
                sourceClientKey === SKILL_INDEX_CLIENT_KEY
                    ? "SKILL.md"
                    : displayFiles
                          .find(
                              ({ clientKey: candidate }) =>
                                  candidate === sourceClientKey
                          )
                          ?.name.trim() || "Untitled reference",
        }));

    const lifecyclePending =
        unpublishMutation.isPending || deleteSkillMutation.isPending;

    const retryBlockedRecovery =
        saveRecovery?.kind === "retry-blocked" ? saveRecovery : null;
    const retryBlocked = retryBlockedRecovery !== null;
    const publishUnknown = saveRecovery?.kind === "publish-status-unknown";
    const publishRejected =
        saveRecovery?.kind === "draft-saved-publish-rejected";
    const publishUnpublishedElsewhere =
        saveRecovery?.kind === "publish-unpublished-elsewhere";
    useEffect(() => {
        if (saveRecovery === null) setReloadFailed(false);
    }, [saveRecovery]);
    useEffect(() => {
        if (retryBlocked) setPublishOpen(false);
    }, [retryBlocked]);
    useEffect(() => {
        if (publishUnknown || publishRejected || publishUnpublishedElsewhere) {
            setPublishOpen(false);
        }
    }, [publishRejected, publishUnknown, publishUnpublishedElsewhere]);
    const isDraft = skill.status === "inactive";
    const savesBlocked = saveConflict !== null || saveRecovery !== null;
    const lifecycleBlocked =
        isSaving || savesBlocked || isResolvingRecovery || lifecyclePending;
    const recoveryChoice =
        saveRecovery &&
        saveRecovery.kind !== "retry-blocked" &&
        saveRecovery.kind !== "publish-status-unknown" &&
        saveRecovery.kind !== "draft-saved-publish-rejected" &&
        saveRecovery.kind !== "publish-unpublished-elsewhere"
            ? saveRecovery
            : null;

    const handleReloadLatest = async () => {
        const targetSkillId = detailsQueryKey[1];
        setReloadFailed(false);
        try {
            const result = await reloadLatest();
            if (
                result === "publish-applied" &&
                activeDetailsQueryKeyRef.current[1] === targetSkillId
            ) {
                toast.success("Skill published");
            }
        } catch {
            setReloadFailed(true);
        }
    };

    const handleKeepLocal = async (remoteToken: string) => {
        const targetSkillId = detailsQueryKey[1];
        const intent = saveConflict?.intent;
        if (intent === undefined) return;
        const successMessage =
            intent === "draft"
                ? "Draft saved"
                : skill.status === "active"
                  ? "Skill saved"
                  : "Skill published";
        try {
            await keepLocal(remoteToken);
            if (activeDetailsQueryKeyRef.current[1] === targetSkillId) {
                if (intent === "publish") setPublishOpen(false);
                toast.success(successMessage);
            }
        } catch {
            return;
        }
    };

    const handleReloadServerFile = async (clientKey: string) => {
        try {
            await reloadServerFile(clientKey);
        } catch {
            return;
        }
    };

    const discardChanges = () => {
        discard();
        clearSaveError();
        if (hasPendingSnapshot) {
            toast.warning("Unsaved changes discarded", {
                description:
                    "This version is still not recorded. Save to record it.",
            });
            return;
        }
        toast.success("Changes discarded");
    };

    const saveDraft = async () => {
        const targetSkillId = detailsQueryKey[1];
        clearSaveError();
        try {
            const result = await save("draft");
            if (activeDetailsQueryKeyRef.current[1] === targetSkillId) {
                toast.success(
                    result.noChanges ? "No changes to save" : "Draft saved"
                );
            }
        } catch {
            return;
        }
    };

    const canSaveDraft =
        isDirty &&
        !isSaving &&
        !savesBlocked &&
        !hasValidationErrors &&
        !scopeBlocked;
    const publishBlocked =
        savesBlocked ||
        hasValidationErrors ||
        brokenReferenceCount > 0 ||
        scopeBlocked;
    const modalOpen =
        publishOpen ||
        renameOpen ||
        unpublishOpen ||
        historyOpen ||
        saveConflict !== null ||
        recoveryChoice !== null ||
        renameRequest !== null ||
        pendingReference !== undefined ||
        deleteSkillTarget?.skillId === skill.id;
    const draftShortcutRef = useRef<() => boolean>(() => false);
    useEffect(() => {
        draftShortcutRef.current = () => {
            if (modalOpen) return false;
            if (canSaveDraft) void saveDraft();
            return true;
        };
    });
    useEffect(() => {
        if (!isDraft) return;
        const handleDraftShortcut = (event: KeyboardEvent) => {
            if (event.key.toLowerCase() !== "s") return;
            if (event.shiftKey || event.altKey) return;
            if (!(event.metaKey || event.ctrlKey)) return;
            if (!draftShortcutRef.current()) return;
            event.preventDefault();
        };
        document.addEventListener("keydown", handleDraftShortcut);
        return () =>
            document.removeEventListener("keydown", handleDraftShortcut);
    }, [isDraft]);

    const savePublished = async () => {
        const targetSkillId = detailsQueryKey[1];
        clearSaveError();
        try {
            await save("publish");
            if (activeDetailsQueryKeyRef.current[1] === targetSkillId) {
                toast.success("Skill saved");
            }
        } catch {
            return;
        }
    };

    const confirmPublish = async () => {
        const targetSkillId = detailsQueryKey[1];
        clearSaveError();
        try {
            await save("publish");
            if (activeDetailsQueryKeyRef.current[1] === targetSkillId) {
                setPublishOpen(false);
                toast.success("Skill published");
            }
        } catch {
            return;
        }
    };

    const retryPublish = async () => {
        const targetSkillId = detailsQueryKey[1];
        try {
            const result = await retryPublishOnly({
                acceptsDuplicateVersionRisk: true,
            });
            if (activeDetailsQueryKeyRef.current[1] === targetSkillId) {
                if (result?.kind === "rejected") {
                    toast.error("Publish failed", {
                        description: result.reason
                            ? `${result.reason} Your edits are kept.`
                            : "Your edits are kept.",
                    });
                    return;
                }
                if (result?.kind === "applied") {
                    toast.success("Skill published");
                }
            }
        } catch {
            if (activeDetailsQueryKeyRef.current[1] === targetSkillId) {
                toast.error("Publish still could not be confirmed", {
                    description: "Your edits are kept.",
                });
            }
        }
    };

    const reopenPublishAfterRejection = () => {
        dismissPublishFailure();
        setPublishOpen(true);
    };

    const requestReferenceDelete = (clientKey: string) => {
        if (isSaving) return;
        if (getReferenceCount(clientKey) > 0) {
            setPendingReferenceDelete(clientKey);
            return;
        }
        deleteFile(clientKey);
    };

    const jumpToBrokenReference = (typedTarget: string) => {
        markdownEditorRef.current?.scrollToMention(typedTarget);
    };

    // Headings outlive the editor that emitted them, and the index content can
    // change while a reference is selected, so a carried-over list would describe
    // a document the remounted editor never held.
    useEffect(() => {
        if (state.selectedClientKey !== SKILL_INDEX_CLIENT_KEY) {
            setOutlineHeadings([]);
        }
    }, [state.selectedClientKey]);

    const updateOutlineHeadings = (headings: readonly EditorHeading[]) => {
        setOutlineHeadings(
            headings
                .filter(
                    ({ level, isRoot, label }) =>
                        level === 2 && isRoot && label !== ""
                )
                .map(({ label, index }) => ({ label, index }))
        );
    };

    const jumpToOutlineHeading = ({ index }: SkillFileTreeOutlineHeading) => {
        const jumped = markdownEditorRef.current?.scrollToHeading({
            level: 2,
            index,
        });
        if (!jumped) {
            toast.info("Couldn't open that heading", {
                description: "Try choosing it again.",
            });
        }
    };

    return (
        <AdminPageContent className="@container/workspace -m-4 flex h-[calc(100dvh-var(--header-height))] min-h-0 flex-col overflow-hidden md:-m-6">
            <header className="flex flex-wrap items-center gap-3 border-b px-4 py-2.5">
                <div className="flex min-w-0 flex-1 items-center gap-2.5">
                    <h1 className="font-heading min-w-0 text-xl font-semibold">
                        <button
                            type="button"
                            onClick={(event) => {
                                if (lifecycleBlocked) return;
                                renameReturnFocusRef.current =
                                    event.currentTarget;
                                setRenameOpen(true);
                            }}
                            title="Rename skill"
                            disabled={lifecycleBlocked}
                            className="hover:bg-muted/50 focus-visible:ring-ring/50 -mx-1.5 max-w-[calc(100%+0.75rem)] truncate rounded-md px-1.5 py-0.5 text-left transition-colors outline-none focus-visible:ring-2"
                        >
                            {state.buffer.skill.name}
                        </button>
                    </h1>
                    <Badge variant={SKILL_STATUS_MAP[skill.status].variant}>
                        {SKILL_STATUS_MAP[skill.status].label}
                    </Badge>
                    <span
                        className={`flex shrink-0 items-center gap-1.5 text-xs font-medium ${
                            hasDiscardableChanges || hasPendingSnapshot
                                ? "text-brand-copper"
                                : "text-muted-foreground"
                        }`}
                    >
                        <span>{changeSummary}</span>
                        {hasPendingSnapshot && (
                            <>
                                <span aria-hidden="true">&middot;</span>
                                <span id={SNAPSHOT_DEBT_SUMMARY_ID}>
                                    Version not recorded
                                </span>
                            </>
                        )}
                    </span>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <SkillSettingsPopover
                        scopeKind={scopeKind}
                        onScopeKindChange={(nextScopeKind) => {
                            updateFields({ scopeKind: nextScopeKind });
                        }}
                        organizationId={state.buffer.skill.organizationId}
                        onOrganizationChange={(organizationId) => {
                            updateFields({ organizationId });
                        }}
                        organizationGroupId={
                            state.buffer.skill.organizationGroupId
                        }
                        onOrganizationGroupChange={(organizationGroupId) => {
                            updateFields({ organizationGroupId });
                        }}
                        onScopeValidityChange={setScopeValidity}
                        isCore={state.buffer.skill.isCore}
                        onIsCoreChange={(isCore) => updateFields({ isCore })}
                        coreCount={coreCount}
                        coreCountScope={
                            coreCountScope === "global" ? "global" : "org"
                        }
                        coreError={
                            coreCountError
                                ? "Core skill count unavailable"
                                : undefined
                        }
                        coreIncludesThisSkill={coreIncludesThisSkill}
                        isSaving={isSaving}
                        isDirty={
                            scopeDirty ||
                            state.buffer.skill.isCore !==
                                state.baseline.skill.isCore
                        }
                    />
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={discardChanges}
                        aria-label={
                            hasPendingSnapshot
                                ? "Discard unsaved changes and keep the unrecorded version"
                                : undefined
                        }
                        aria-describedby={
                            hasPendingSnapshot
                                ? SNAPSHOT_DEBT_SUMMARY_ID
                                : undefined
                        }
                        disabled={
                            !hasDiscardableChanges || isSaving || retryBlocked
                        }
                    >
                        Discard
                    </Button>
                    {isDraft ? (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => void saveDraft()}
                                aria-describedby={
                                    validationDescription || undefined
                                }
                                aria-disabled={!canSaveDraft || undefined}
                                disabled={!canSaveDraft}
                            >
                                {isSaving ? "Saving…" : "Save draft"}
                            </Button>
                            <Button
                                type="button"
                                onClick={() => setPublishOpen(true)}
                                aria-describedby={
                                    publishBlockDescription || undefined
                                }
                                aria-disabled={
                                    isSaving || publishBlocked || undefined
                                }
                                disabled={isSaving || publishBlocked}
                            >
                                {isDirty ? "Save & publish" : "Publish"}
                            </Button>
                        </>
                    ) : (
                        <Button
                            type="button"
                            onClick={() => void savePublished()}
                            aria-describedby={
                                publishBlockDescription || undefined
                            }
                            aria-disabled={
                                !isDirty || isSaving || publishBlocked
                                    ? true
                                    : undefined
                            }
                            disabled={!isDirty || isSaving || publishBlocked}
                        >
                            {isSaving ? "Saving…" : "Save"}
                        </Button>
                    )}
                    <DropdownMenu>
                        <DropdownMenuTrigger
                            render={
                                <Button
                                    ref={actionsTriggerRef}
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    aria-label="Actions"
                                    disabled={lifecycleBlocked}
                                >
                                    <MoreHorizontal />
                                </Button>
                            }
                        />
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={() => {
                                    if (lifecycleBlocked) return;
                                    renameReturnFocusRef.current =
                                        actionsTriggerRef.current;
                                    // The menu restores focus to its trigger as
                                    // it closes; opening in the same task lets
                                    // that restore steal focus from the dialog.
                                    queueMicrotask(() => setRenameOpen(true));
                                }}
                            >
                                <Pencil />
                                Rename skill
                            </DropdownMenuItem>
                            {skill.status === "active" && (
                                <DropdownMenuItem
                                    onClick={() => {
                                        if (lifecycleBlocked) return;
                                        unpublishMutation.reset();
                                        setUnpublishOpen(true);
                                    }}
                                >
                                    <Archive />
                                    Unpublish
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                variant="destructive"
                                onClick={() => {
                                    if (lifecycleBlocked) return;
                                    deleteSkillMutation.reset();
                                    setDeleteSkillTarget({
                                        skillId: skill.id,
                                        name: state.buffer.skill.name.trim(),
                                        isPublished: skill.status === "active",
                                    });
                                }}
                            >
                                <Trash2 />
                                Delete skill
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                {validationSummary && (
                    <p
                        id={VALIDATION_SUMMARY_ID}
                        className="text-destructive basis-full text-right text-xs"
                    >
                        {validationSummary}
                    </p>
                )}
                {scopeBlockReason && (
                    <p
                        id={SCOPE_BLOCK_SUMMARY_ID}
                        role="status"
                        className="text-destructive basis-full text-right text-xs"
                    >
                        {scopeBlockReason}
                    </p>
                )}
            </header>

            {retryBlocked && (
                <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2">
                    <p
                        id={RETRY_BLOCKED_SUMMARY_ID}
                        role="status"
                        className="text-brand-copper text-sm"
                    >
                        {reloadFailed
                            ? "Still could not load the latest version. Your edits are kept."
                            : retryBlockedRecovery.completedOperations.length >
                                0
                              ? "Some changes were confirmed saved. The rest could not be confirmed, and the latest version could not be loaded. Your edits are kept."
                              : "The save outcome could not be confirmed because the latest version could not be loaded. Your edits are kept."}
                    </p>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void handleReloadLatest()}
                        aria-describedby={RETRY_BLOCKED_SUMMARY_ID}
                        disabled={isResolvingRecovery}
                    >
                        Reload latest
                    </Button>
                </div>
            )}

            {publishUnknown && (
                <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2">
                    <p
                        id={PUBLISH_UNKNOWN_SUMMARY_ID}
                        role="status"
                        className="text-brand-copper text-sm"
                    >
                        {canRetryPublishOnly
                            ? "The publish could not be confirmed, so it is still blocked. Retrying sends publish only and may create another version in Claude."
                            : reloadFailed
                              ? "The latest status could not be loaded, so the publish is still unconfirmed. Check again before retrying."
                              : "The publish could not be confirmed. Check the latest status before retrying."}
                    </p>
                    {canRetryPublishOnly ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void retryPublish()}
                            aria-describedby={PUBLISH_UNKNOWN_SUMMARY_ID}
                            disabled={isSaving || isResolvingRecovery}
                        >
                            Retry publish
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void handleReloadLatest()}
                            aria-describedby={PUBLISH_UNKNOWN_SUMMARY_ID}
                            disabled={isSaving || isResolvingRecovery}
                        >
                            Check status
                        </Button>
                    )}
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={dismissPublishFailure}
                        aria-describedby={PUBLISH_UNKNOWN_SUMMARY_ID}
                        disabled={isSaving || isResolvingRecovery}
                    >
                        Dismiss
                    </Button>
                </div>
            )}

            {publishUnpublishedElsewhere && (
                <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2">
                    <p
                        id={PUBLISH_UNPUBLISHED_SUMMARY_ID}
                        role="status"
                        className="text-brand-copper text-sm"
                    >
                        This skill is no longer published. It was unpublished
                        elsewhere while your publish was in flight. Your edits
                        are kept.
                    </p>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={dismissPublishFailure}
                        aria-describedby={PUBLISH_UNPUBLISHED_SUMMARY_ID}
                        disabled={isSaving || isResolvingRecovery}
                    >
                        Dismiss
                    </Button>
                </div>
            )}

            {publishRejected && (
                <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2">
                    <p
                        id={PUBLISH_REJECTED_SUMMARY_ID}
                        role="status"
                        className="text-destructive text-sm"
                    >
                        Draft saved; publish failed.
                        {publishFailureReason ? ` ${publishFailureReason}` : ""}
                    </p>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={reopenPublishAfterRejection}
                        aria-describedby={PUBLISH_REJECTED_SUMMARY_ID}
                        disabled={isSaving || isResolvingRecovery}
                    >
                        Try publish again
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={dismissPublishFailure}
                        aria-describedby={PUBLISH_REJECTED_SUMMARY_ID}
                        disabled={isSaving || isResolvingRecovery}
                    >
                        Dismiss
                    </Button>
                </div>
            )}

            {saveError !== null && (
                <p
                    role="alert"
                    className="text-destructive border-b px-4 py-2 text-sm"
                >
                    Failed to save. Try again.
                </p>
            )}

            {saveStatus.kind === "saved-with-refresh-warning" && (
                <p
                    role="status"
                    className="text-brand-copper border-b px-4 py-2 text-sm"
                >
                    Saved. The latest status could not be refreshed &mdash;
                    reload to see it.
                </p>
            )}

            {brokenReferenceCount > 0 && (
                <p
                    id={BROKEN_REFERENCES_SUMMARY_ID}
                    className="text-destructive border-b px-4 py-2 text-sm"
                >
                    {brokenReferenceCount} @-reference
                    {brokenReferenceCount === 1 ? "" : "s"} could not be
                    resolved. Repair them before publishing.
                </p>
            )}

            <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden @min-[52rem]/workspace:grid-cols-[244px_minmax(0,1fr)]">
                <aside className="bg-muted/20 flex min-h-0 flex-col border-b @min-[52rem]/workspace:border-r @min-[52rem]/workspace:border-b-0">
                    <SkillFileTree
                        indexEntry={{
                            clientKey: SKILL_INDEX_CLIENT_KEY,
                            name: "SKILL.md",
                            isDirty: indexDirty,
                            isInvalid:
                                Boolean(descriptionError) ||
                                state.buffer.skill.name.trim() === "",
                            brokenReferenceCount: getBrokenTargets(
                                SKILL_INDEX_CLIENT_KEY
                            ).length,
                        }}
                        referenceEntries={displayFiles.map((file) => ({
                            clientKey: file.clientKey,
                            name: file.name,
                            isDirty: isSkillEditorFileDirty(
                                state,
                                file.clientKey
                            ),
                            isInvalid: Boolean(
                                referenceErrors.get(file.clientKey)?.name ||
                                referenceErrors.get(file.clientKey)?.content
                            ),
                            isPlaceholder: file.isPlaceholder,
                            brokenReferenceCount: getBrokenTargets(
                                file.clientKey
                            ).length,
                        }))}
                        selectedClientKey={state.selectedClientKey}
                        onSelect={selectFile}
                        outline={{
                            headings: outlineHeadings,
                            onJump: jumpToOutlineHeading,
                        }}
                        onAddReference={() => {
                            addFile();
                            setFocusRequest((request) => request + 1);
                        }}
                    />
                    <SkillVersionHistory
                        versions={versions}
                        isLoading={isVersionsLoading}
                        isError={Boolean(versionsError)}
                        onRetry={() => {
                            void retryVersions();
                        }}
                        onSelectVersion={(versionId) => {
                            setHistoryVersionId(versionId);
                            setHistoryOpen(true);
                        }}
                        onViewAll={() => {
                            setHistoryVersionId(null);
                            setHistoryOpen(true);
                        }}
                    />
                </aside>

                <section
                    ref={paneRegionRef}
                    aria-label="Skill editor"
                    className="min-h-0 min-w-0"
                >
                    {state.selectedClientKey === SKILL_INDEX_CLIENT_KEY ? (
                        <SkillEditorPane
                            kind="index"
                            clientKey={SKILL_INDEX_CLIENT_KEY}
                            resetEpoch={state.resetEpoch}
                            content={state.buffer.skill.content}
                            onContentChange={(content) =>
                                updateFields({ content })
                            }
                            status={selectedStatus}
                            isSaving={isSaving}
                            savedAt={state.baseline.skill.updatedAt}
                            suggestions={referenceNames}
                            recognitionVocabulary={recognitionVocabulary}
                            editorRef={markdownEditorRef}
                            brokenTargets={getBrokenTargets(
                                SKILL_INDEX_CLIENT_KEY
                            ).map(({ typedTarget }) => typedTarget)}
                            onJump={jumpToBrokenReference}
                            inboundLinks={inboundLinksFor(
                                SKILL_INDEX_CLIENT_KEY
                            )}
                            onSelectSource={selectFile}
                            description={state.buffer.skill.description}
                            onDescriptionChange={(description) =>
                                updateFields({ description })
                            }
                            descriptionError={descriptionError}
                            onHeadingsChange={updateOutlineHeadings}
                        />
                    ) : selectedFile ? (
                        <SkillEditorPane
                            kind="reference"
                            clientKey={selectedFile.clientKey}
                            resetEpoch={state.resetEpoch}
                            content={selectedFile.content}
                            onContentChange={(content) =>
                                updateFile(selectedFile.clientKey, { content })
                            }
                            status={selectedStatus}
                            isSaving={isSaving}
                            savedAt={selectedBaseline?.updatedAt}
                            suggestions={referenceNames}
                            recognitionVocabulary={recognitionVocabulary}
                            editorRef={markdownEditorRef}
                            brokenTargets={getBrokenTargets(
                                selectedFile.clientKey
                            ).map(({ typedTarget }) => typedTarget)}
                            onJump={jumpToBrokenReference}
                            inboundLinks={inboundLinksFor(
                                selectedFile.clientKey
                            )}
                            onSelectSource={selectFile}
                            name={selectedFile.name}
                            onNameChange={(name) =>
                                updateFile(selectedFile.clientKey, { name })
                            }
                            onNameCommit={() => {
                                window.setTimeout(
                                    () =>
                                        requestRenameCommit(
                                            selectedFile.clientKey
                                        ),
                                    0
                                );
                            }}
                            onNameCancel={(name) => {
                                if (selectedFile.renameOrigin !== null) {
                                    cancelRename(selectedFile.clientKey);
                                    return;
                                }
                                updateFile(selectedFile.clientKey, { name });
                            }}
                            renameOrigin={selectedFile.renameOrigin}
                            nameError={
                                selectedReferenceErrors?.rename ??
                                selectedReferenceErrors?.name
                            }
                            contentError={selectedReferenceErrors?.content}
                            onDelete={() =>
                                requestReferenceDelete(selectedFile.clientKey)
                            }
                        />
                    ) : null}
                </section>
            </div>

            <SaveConflictDialog
                conflict={saveConflict}
                isResolving={isSaving || isResolvingRecovery}
                onReloadConflicts={reloadConflicts}
                onKeepLocal={(remoteToken) => void handleKeepLocal(remoteToken)}
                onKeepEditing={() => {
                    clearSaveConflict();
                    setPublishOpen(false);
                }}
            />

            <SaveRecoveryDialog
                recovery={recoveryChoice}
                siblingNames={displayFiles
                    .filter(
                        ({ clientKey }) =>
                            clientKey !== recoveryChoice?.clientKey
                    )
                    .map(({ name }) => name)}
                isResolving={isResolvingRecovery}
                onRecreateUnderSameName={recreateUnderSameName}
                onRenameAndRecreate={(clientKey, name) =>
                    void renameAndRecreate(clientKey, name)
                }
                onDiscardLocal={discardLocal}
                onReloadServerFile={(clientKey) =>
                    void handleReloadServerFile(clientKey)
                }
                onKeepLocalAndRename={(clientKey, name) =>
                    void keepLocalAndRename(clientKey, name)
                }
            />

            <SkillPublishDialog
                open={publishOpen}
                skillName={state.buffer.skill.name.trim()}
                changes={saveChanges}
                isSaving={isSaving}
                isBlocked={publishBlocked}
                blockDescriptionId={publishBlockDescription || undefined}
                hasError={saveError !== null}
                onOpenChange={setPublishOpen}
                onConfirm={() => void confirmPublish()}
            />

            <SkillRenameDialog
                open={renameOpen}
                skillId={skill.id}
                currentName={state.buffer.skill.name}
                maxLength={skill.name_max_length}
                onOpenChange={setRenameOpen}
                finalFocus={renameReturnFocusRef}
                onRename={(name) => updateFields({ name })}
            />

            <Dialog
                open={unpublishOpen}
                onOpenChange={(open) => {
                    if (!unpublishMutation.isPending) {
                        setUnpublishOpen(open);
                        if (!open) unpublishMutation.reset();
                    }
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Unpublish skill?</DialogTitle>
                        <DialogDescription>
                            Unpublish &quot;{state.buffer.skill.name.trim()}
                            &quot;? AI Chat will no longer be able to use this
                            skill.
                        </DialogDescription>
                    </DialogHeader>
                    <p className="text-muted-foreground text-sm">
                        The skill will revert to draft status. You can publish
                        it again at any time.
                    </p>
                    {unpublishMutation.isError && (
                        <p role="alert" className="text-destructive text-sm">
                            Failed to unpublish. Try again.
                        </p>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setUnpublishOpen(false);
                                unpublishMutation.reset();
                            }}
                            disabled={unpublishMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() =>
                                unpublishMutation.mutate({
                                    skillId: skill.id,
                                    detailsQueryKey,
                                })
                            }
                            disabled={unpublishMutation.isPending}
                        >
                            {unpublishMutation.isPending
                                ? "Unpublishing…"
                                : "Unpublish"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={deleteSkillTarget?.skillId === skill.id}
                onOpenChange={(open) => {
                    if (!open && !deleteSkillMutation.isPending) {
                        setDeleteSkillTarget(null);
                        deleteSkillMutation.reset();
                    }
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete skill?</DialogTitle>
                        <DialogDescription>
                            {deleteSkillTarget?.isPublished
                                ? "This skill is currently published and may be in use by agents. Delete it permanently?"
                                : `Delete "${deleteSkillTarget?.name ?? ""}" permanently?`}
                        </DialogDescription>
                    </DialogHeader>
                    <p className="text-muted-foreground text-sm">
                        This action cannot be undone.
                    </p>
                    {deleteSkillMutation.isError && (
                        <p role="alert" className="text-destructive text-sm">
                            Failed to delete. Try again.
                        </p>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setDeleteSkillTarget(null);
                                deleteSkillMutation.reset();
                            }}
                            disabled={deleteSkillMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() => {
                                if (deleteSkillTarget) {
                                    deleteSkillMutation.mutate({
                                        skillId: deleteSkillTarget.skillId,
                                        detailsQueryKey,
                                    });
                                }
                            }}
                            disabled={deleteSkillMutation.isPending}
                        >
                            {deleteSkillMutation.isPending
                                ? "Deleting…"
                                : "Delete"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ReferenceRenameDialog
                open={renameRequest !== null && pendingReference === undefined}
                clientKey={renameRequest?.clientKey ?? ""}
                origin={renameRequest?.origin ?? ""}
                destination={renameRequest?.destination ?? ""}
                occurrenceCount={renameRequest?.referenceCount ?? 0}
                isPending={isSaving}
                onConfirm={acknowledgeRename}
                onCancel={() => {
                    if (renameRequest) cancelRename(renameRequest.clientKey);
                }}
            />

            <ReferenceDeleteDialog
                open={pendingReference !== undefined}
                clientKey={pendingReference?.clientKey ?? ""}
                name={
                    pendingReference?.renameOrigin ??
                    pendingReference?.name ??
                    ""
                }
                occurrenceCount={
                    pendingReference
                        ? getReferenceCount(pendingReference.clientKey)
                        : 0
                }
                isPending={isSaving}
                onConfirm={(clientKey) => {
                    deleteFile(clientKey);
                    setPendingReferenceDelete(null);
                }}
                onCancel={() => setPendingReferenceDelete(null)}
            />

            <VersionDiffModal
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                skillId={skill.id}
                initialVersionId={historyVersionId}
            />
        </AdminPageContent>
    );
};

export default AdminSkillDetails;
